import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import {
  MdOutlineSettingsBackupRestore,
  MdOutlineProductionQuantityLimits,
} from "react-icons/md";
import Modal from "../components/ui/Modal";
import {
  useGetProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
} from "apis/products";
import {
  useAddCampaignEventsMutation,
  useDeleteCampaignEventsMutation,
} from "apis/campaignEvent";
import { FaPlus } from "react-icons/fa6";
import ProductEdit from "components/products/ProductEdit";
import ProductView from "components/products/ProductView";
import Table from "components/ui/Table";
import FilterBar from "components/ui/FilterBar";
import moment from "moment-timezone";

const Products = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRestoreOpen, setIsRestoreOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState("active");

  // Top-of-page filters
  const [contractorFilter, setContractorFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");

  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useGetProductsQuery({ showAll: true });

  const { mutateAsync: createProduct, isLoading: isCreateLoading } =
    useCreateProductMutation();

  const { mutate: updateProduct, isLoading: isUpdateLoading } =
    useUpdateProductMutation();

  // Scheduled-work products create their campaign event first (quiet — the
  // product toast covers success; errors like a duplicate 409 still toast).
  const addCampaignEvent = useAddCampaignEventsMutation({
    onSuccess: () => {},
  });
  const deleteCampaignEvent = useDeleteCampaignEventsMutation({
    onSuccess: () => {},
    onError: () => {},
  });

  // Distinct filter options derived from the current dataset
  const contractorOptions = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    data.forEach((item) => {
      const c = item.contractorCompanyId;
      if (c && c._id) {
        map.set(c._id, c.name);
      }
    });
    return Array.from(map, ([value, label]) => ({ value, label })).sort(
      (a, b) => (a.label || "").localeCompare(b.label || ""),
    );
  }, [data]);

  // Who owns the product: the Beleef catalogue, or a company that added it
  // itself from their profile tab. "beleef" is a sentinel, not an id — the
  // catalogue rows have no companyId to key on.
  const ownerOptions = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    let hasCatalogue = false;
    data.forEach((item) => {
      const c = item.companyId;
      if (c && c._id) map.set(c._id, c.name);
      else hasCatalogue = true;
    });
    const companies = Array.from(map, ([value, label]) => ({
      value,
      label,
    })).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    return hasCatalogue
      ? [{ value: "beleef", label: "Beleef" }, ...companies]
      : companies;
  }, [data]);

  const categoryOptions = useMemo(() => {
    if (!data) return [];
    const set = new Set();
    data.forEach((item) => {
      if (item.category) set.add(item.category);
    });
    return Array.from(set).sort();
  }, [data]);

  const typeOptions = useMemo(() => {
    if (!data) return [];
    const set = new Set();
    data.forEach((item) => {
      if (item.type) set.add(item.type);
    });
    return Array.from(set).sort();
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((item) => {
      // Active / archive
      const matchesView =
        viewMode === "active" ? !item.isDeleted : item.isDeleted;
      if (!matchesView) return false;

      // Contractor company
      if (
        contractorFilter &&
        item.contractorCompanyId?._id !== contractorFilter
      ) {
        return false;
      }

      // Category
      if (categoryFilter && item.category !== categoryFilter) {
        return false;
      }

      // Type
      if (typeFilter && item.type !== typeFilter) {
        return false;
      }

      // Owner — "beleef" means the catalogue rows, which carry no companyId
      if (ownerFilter) {
        if (ownerFilter === "beleef") {
          if (item.companyId) return false;
        } else if (item.companyId?._id !== ownerFilter) {
          return false;
        }
      }

      return true;
    });
  }, [
    data,
    viewMode,
    contractorFilter,
    categoryFilter,
    typeFilter,
    ownerFilter,
  ]);

  const hasActiveFilters =
    contractorFilter || categoryFilter || typeFilter || ownerFilter;

  const clearFilters = () => {
    setContractorFilter("");
    setCategoryFilter("");
    setTypeFilter("");
    setOwnerFilter("");
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Product Name",
        isFilter: true,
        minWidth: "180px",
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: (row) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
              row.type === "subscription"
                ? "bg-indigo-100 text-indigo-800"
                : row.type === "marketing"
                  ? "bg-orange-100 text-orange-800"
                  : "bg-emerald-100 text-emerald-800"
            }`}
          >
            {row.type}
          </span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: (row) => <span className="capitalize">{row.category}</span>,
        isFilter: true,
      },
      {
        accessorKey: "companyId",
        header: "Owner",
        cell: (row) => {
          const company = row.companyId;
          // No companyId = a Beleef catalogue product, visible to everyone.
          // Otherwise it's self-serve and only that company sees it.
          if (!company) {
            return (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-lightgray text-darkgray">
                Beleef
              </span>
            );
          }
          return (
            <span
              className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 truncate inline-block max-w-[160px]"
              title={company.name}
            >
              {company.name}
            </span>
          );
        },
        isFilter: false,
        minWidth: "150px",
      },
      {
        accessorKey: "contractorCompanyId",
        header: "Contractor Company",
        cell: (row) => {
          const company = row.contractorCompanyId;
          if (!company) return <span className="text-darkgray">-</span>;
          return (
            <div className="min-w-0 flex items-center gap-3">
              {company.logo && (
                <img
                  src={company.logo}
                  alt={company.name}
                  className="w-8 h-8 rounded-full object-contain flex-shrink-0"
                />
              )}
              <span className="truncate">{company.name}</span>
            </div>
          );
        },
        isFilter: false,
        minWidth: "200px",
      },
      {
        accessorKey: "priceDisplay",
        header: "Price",
        cell: (row) => (
          <span className="font-medium">
            {row.priceDisplay || (row.price ? `$${row.price}` : "Custom")}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "billingCycle",
        header: "Billing",
        cell: (row) => (
          <span className="capitalize">
            {row.type === "subscription" && row.billingCycle
              ? row.billingCycle
              : row.type === "transactional" || row.type === "marketing"
                ? "One-time"
                : "-"}
          </span>
        ),
        isFilter: false,
      },
      ...(viewMode === "archive"
        ? [
            {
              accessorKey: "deletedAt",
              header: "Deleted Date",
              cell: (row) => (
                <span className="text-sm">
                  {row.deletedAt
                    ? moment(row.deletedAt)
                        .tz("Australia/Sydney")
                        .format("D MMM YYYY")
                    : "-"}
                </span>
              ),
              isFilter: false,
            },
          ]
        : []),
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Product"
            >
              <FiEdit2 fontSize={20} />
            </button>
            {viewMode === "active" ? (
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsDeleteOpen(true);
                }}
                className="delete-icon"
                title="Delete Product"
              >
                <FiTrash2 fontSize={20} />
              </button>
            ) : (
              <button
                onClick={() => {
                  setSelectedId(row._id);
                  setIsRestoreOpen(true);
                }}
                className="view-icon"
                title="Restore Product"
              >
                <MdOutlineSettingsBackupRestore fontSize={22} color="green" />
              </button>
            )}
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    [viewMode],
  );

  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading products: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedProduct = data?.find((product) => product._id === selectedId);

  const handleEditSubmit = (formData) => {
    updateProduct({ _id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = async (formData) => {
    const { __campaignEvent, ...productData } = formData;

    // Scheduled work: campaign event first (shared with existing identical
    // work via reuseExisting), then the product pointing at it. If the
    // product fails after a FRESH event was created, the event is deleted
    // again so no orphan is left behind — a shared event belongs to other
    // products and is left alone.
    let campaignEventId = null;
    let eventWasShared = false;
    if (__campaignEvent) {
      try {
        const res = await addCampaignEvent.mutateAsync(__campaignEvent);
        campaignEventId = res?.data?._id || null;
        eventWasShared = !!res?.reused;
      } catch (e) {
        return;
      }
    }

    try {
      await createProduct({
        ...productData,
        ...(campaignEventId ? { campaignEventId } : {}),
      });
      setIsAddOpen(false);
    } catch (e) {
      if (campaignEventId && !eventWasShared) {
        deleteCampaignEvent.mutate(campaignEventId);
      }
    }
  };

  const handleDelete = () => {
    updateProduct(
      {
        ...selectedProduct,
        _id: selectedId,
        isDeleted: true,
        deletedAt: new Date().toISOString(),
      },
      {
        onSuccess: () => {
          setIsDeleteOpen(false);
        },
      },
    );
  };

  const handleRestore = () => {
    updateProduct(
      {
        ...selectedProduct,
        _id: selectedId,
        isDeleted: false,
        deletedAt: null,
      },
      {
        onSuccess: () => {
          setIsRestoreOpen(false);
        },
      },
    );
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddProductButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add Product
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Product"
        className="max-w-3xl"
      >
        <ProductEdit
          initialData={selectedProduct}
          onSubmit={handleEditSubmit}
          existingProducts={data || []}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Product"
        className="max-w-3xl"
      >
        <ProductEdit
          initialData={{}}
          onSubmit={handleCreateSubmit}
          existingProducts={data || []}
        />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Product"
      >
        <div>
          <p className="mb-4">Are you sure you want to delete this product?</p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isUpdateLoading}
            >
              No
            </button>
            <button
              onClick={handleDelete}
              className="black-button"
              disabled={isUpdateLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isRestoreOpen}
        onClose={() => setIsRestoreOpen(false)}
        title="Restore Product"
      >
        <div>
          <p className="mb-4">Are you sure you want to restore this product?</p>
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsRestoreOpen(false)}
              className="gray-button"
              disabled={isUpdateLoading}
            >
              No
            </button>
            <button
              onClick={handleRestore}
              className="black-button"
              disabled={isUpdateLoading}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Product Details"
        className="max-w-3xl"
      >
        <ProductView product={selectedProduct} />
      </Modal>

      <div className="container mx-auto">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <div className="flex gap-2 bg-lightgray rounded-lg p-1 border border-mediumgray">
            <button
              onClick={() => setViewMode("active")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "active"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <MdOutlineProductionQuantityLimits className="w-4 h-4" />
              <span>Active</span>
            </button>
            <button
              onClick={() => setViewMode("archive")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                viewMode === "archive"
                  ? "bg-white text-black shadow-sm"
                  : "text-darkgray hover:text-black"
              }`}
            >
              <FiTrash2 className="w-4 h-4" />
              <span>Archive</span>
            </button>
          </div>

          {/* Filters */}
          <FilterBar
            showClear={hasActiveFilters}
            onClear={clearFilters}
            filters={[
              {
                key: "type",
                value: typeFilter,
                onChange: setTypeFilter,
                allLabel: "All Types",
                options: typeOptions,
                minWidth: "150px",
              },
              {
                key: "category",
                value: categoryFilter,
                onChange: setCategoryFilter,
                allLabel: "All Categories",
                options: categoryOptions,
                minWidth: "160px",
              },
              {
                key: "contractor",
                value: contractorFilter,
                onChange: setContractorFilter,
                allLabel: "All Contractor Companies",
                options: contractorOptions,
                minWidth: "200px",
              },
              {
                key: "owner",
                value: ownerFilter,
                onChange: setOwnerFilter,
                allLabel: "All Owners",
                options: ownerOptions,
                minWidth: "190px",
              },
            ]}
          />
        </div>

        <Table
          title="Products"
          columns={columns}
          data={filteredData}
          pageSize={pageSize}
          ButtonCmp={viewMode === "active" ? AddProductButton : undefined}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default Products;
